import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Link } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  quotaGroup: {
    display: 'flex',
    alignItems: 'center',
    background: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.grayBorder}`
  },
  quotaGroupError: {
    border: `1px solid ${theme.colors.redAccessible}`
  },
  quotaInput: {
    border: 'none',
    outline: 'none',
    textAlign: 'right',
    width: 60,
    fontSize: '1rem',
    padding: theme.marginBase,
    background: theme.colors.white,
    '&::-webkit-outer-spin-button': {
      display: 'none',
    },
    '&::-webkit-inner-spin-button': {
      display: 'none',
    },
    '-moz-appearance': 'textfield',
  },
  unit: {
    color: theme.colors.inkTertiary,
    whiteSpace: 'nowrap',
    backgroundColor: theme.colors.grayBackground,
    borderLeft: `1px solid ${theme.colors.grayBorder}`,
    fontWeight: 400,
    padding: [[6, 12]],
    display: 'flex',
    alignItems: 'center',
    height: theme.height.small
  },
  label: {
    whiteSpace: 'nowrap',
  },
  minmaxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  minmaxInputGroup: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.borderRadius.std,
    border: `1px solid ${theme.colors.grayBorder}`,
    height: theme.height.small,
  },
  minmaxInputGroupError: {
    border: `1px solid ${theme.colors.redAccessible}`
  },
  minmaxLabel: {
    color: theme.colors.inkTertiary,
    fontSize: 13,
    marginRight: theme.marginBase,
    whiteSpace: 'nowrap',
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    height: theme.height.small,
    width: theme.height.small,
  },
  minmaxInput: {
    border: 'none',
    outline: 'none',
    fontSize: '1rem',
    maxWidth: 64,
    background: theme.colors.white,
    textAlign: 'center',
    '&::-webkit-outer-spin-button': {
      display: 'none',
    },
    '&::-webkit-inner-spin-button': {
      display: 'none',
    },
    '-moz-appearance': 'textfield',
  },
  deleteIcon: {
    color: theme.colors.inkSecondary,
    fontSize: theme.iconSize.regular,
    marginLeft: theme.marginBase,
    cursor: 'pointer',
  },
}));

const InputContent = ({
  value,
  min,
  max,
  onChange,
  onMinChange,
  onMaxChange,
  onRemove,
  isParticipantCountCorrect,
  rangeIsInvalid
}: {
  value: number | '';
  min: number | null;
  max: number | null;
  onChange: (value: number) => void;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  onRemove: () => void;
  isParticipantCountCorrect?: boolean;
  rangeIsInvalid?: boolean;
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classnames(classes.row)}>
      <div className={classnames(classes.quotaGroup)}>
        <input
          type="number"
          value={value}
          onChange={(e) => {
            const val = e.target.value;
            onChange(val === '' ? 0 : parseInt(val));
          }}
          className={classes.quotaInput}
          placeholder="ex: 30"
        />
        <div className={classes.unit}>{t('editSession.targeting.quotas.unit') || 'people'}</div>
      </div>
      <span className={classes.label}>{t('editSession.targeting.quotas.ageRangeFrom')}</span>
      <div className={classes.minmaxGroup}>
        <div className={classnames(classes.minmaxInputGroup, {
          [classes.minmaxInputGroupError]: rangeIsInvalid
        })}
        >
          <input
            type="number"
            value={min ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onMinChange(val === '' ? 0 : parseInt(val));
            }}
            className={classes.minmaxInput}
          />
        </div>
        <span className={classes.label}>{t('editSession.targeting.quotas.ageRangeTo')}</span>
        <div className={classnames(classes.minmaxInputGroup, {
          [classes.minmaxInputGroupError]: rangeIsInvalid
        })}
        >
          <input
            type="number"
            placeholder={max === null ? '∞' : ''}
            value={max ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onMaxChange(val === '' ? 0 : parseInt(val));
            }}
            className={classes.minmaxInput}
          />
        </div>
      </div>
      <Link className={classes.deleteIcon} onClick={onRemove} type="button">x</Link>
    </div>
  );
};

export const CriterionRangeQuotaInput = ({
    value,
    min,
    max,
    rangeIsInvalid,
    onChange,
    onMinChange,
    onMaxChange,
    onRemove
  }: {
    value: number | '';
    min: number | null;
    max: number | null;
    rangeIsInvalid?: boolean;
    onChange: (value: number) => void;
    onMinChange: (value: number) => void;
    onMaxChange: (value: number) => void;
    onRemove: () => void;
}) => {
  return (
    <InputContent
      value={value}
      min={min}
      max={max}
      onChange={onChange}
      onMinChange={onMinChange}
      onMaxChange={onMaxChange}
      onRemove={onRemove}
      rangeIsInvalid={rangeIsInvalid}
    />
  );
};
